import React, { useState, useEffect, useCallback } from 'react';
import Header from '../../components/Header';
import { colors, radius, shadows } from '../../styles/theme';
import { logError } from '../../utils/logError';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001';

const getAuthHeaders = () => {
  const token = localStorage.getItem('access_token');
  return token ? { 'Authorization': `Bearer ${token}` } : {};
};

const EYLEM_RENK = {
  POST: { bg: '#d1ecf1', color: '#0c5460' },
  PUT: { bg: '#fff3cd', color: '#856404' },
  PATCH: { bg: '#fff3cd', color: '#856404' },
  DELETE: { bg: '#f8d7da', color: '#721c24' },
};

const LogKayitlariPage = () => {
  const [logs, setLogs] = useState([]);
  const [toplam, setToplam] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filtreler, setFiltreler] = useState({
    arama: '',
    eylem: '',
    baslangic_tarihi: '',
    bitis_tarihi: '',
    skip: 0,
    limit: 100,
  });
  const [expandedRow, setExpandedRow] = useState(null);

  const loadLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filtreler).forEach(([k, v]) => { if (v) params.set(k, v); });
      const res = await fetch(`${API_URL}/api/activity-logs?${params}`, { headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        setLogs(data.kayitlar || []);
        setToplam(data.toplam || 0);
      } else {
        setLogs([]);
        setToplam(0);
      }
    } catch (e) {
      logError('Log yükleme hatası:', e);
    } finally {
      setLoading(false);
    }
  }, [filtreler]);

  useEffect(() => { loadLogs(); }, [loadLogs]);

  const formatJSON = (str) => {
    if (!str) return null;
    try {
      return JSON.stringify(JSON.parse(str), null, 2);
    } catch {
      return str;
    }
  };

  const formatDateTime = (iso) => {
    if (!iso) return '-';
    try {
      const d = new Date(iso);
      return d.toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' });
    } catch {
      return iso;
    }
  };

  const renderDiff = (log) => {
    const before = log.before_data ? JSON.parse(log.before_data) : null;
    const after = log.after_data ? JSON.parse(log.after_data) : null;
    if (!before && !after) {
      return log.payload ? (
        <pre style={{ background: colors.backgroundAlt, padding: '12px', borderRadius: radius.sm, fontSize: '12px', overflow: 'auto', maxHeight: '300px' }}>
          {formatJSON(log.payload)}
        </pre>
      ) : <div style={{ color: colors.textTertiary, fontSize: '13px', padding: '8px' }}>Detay yok</div>;
    }

    // Field-level diff
    const allKeys = new Set([...(before ? Object.keys(before) : []), ...(after ? Object.keys(after) : [])]);
    const changes = [];
    allKeys.forEach(key => {
      const oldVal = before?.[key];
      const newVal = after?.[key];
      const changed = JSON.stringify(oldVal) !== JSON.stringify(newVal);
      if (changed || !after || !before) {
        changes.push({ key, oldVal, newVal, changed });
      }
    });

    return (
      <div style={{ background: colors.backgroundAlt, padding: '12px', borderRadius: radius.sm, fontSize: '12px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '180px 1fr 1fr', gap: '8px', fontWeight: 'bold', marginBottom: '8px', borderBottom: `1px solid ${colors.border}`, paddingBottom: '6px' }}>
          <div>Alan</div>
          <div style={{ color: '#dc3545' }}>Önceki</div>
          <div style={{ color: '#28a745' }}>Yeni</div>
        </div>
        {changes.map(({ key, oldVal, newVal, changed }) => (
          <div key={key} style={{ display: 'grid', gridTemplateColumns: '180px 1fr 1fr', gap: '8px', padding: '4px 0', borderBottom: '1px solid #f0f0f0', background: changed ? '#fffbe6' : 'transparent' }}>
            <div style={{ fontWeight: '600', color: '#555' }}>{key}</div>
            <div style={{ color: '#dc3545', wordBreak: 'break-all' }}>{oldVal !== null && oldVal !== undefined ? String(oldVal) : '—'}</div>
            <div style={{ color: '#28a745', wordBreak: 'break-all' }}>{newVal !== null && newVal !== undefined ? String(newVal) : '—'}</div>
          </div>
        ))}
      </div>
    );
  };

  const handleFiltreChange = (key, value) => {
    setFiltreler(prev => ({ ...prev, [key]: value, skip: 0 }));
  };

  return (
    <div>
      <Header title="Sistem Logları" />
      <div style={{ padding: '20px', background: colors.background, minHeight: 'calc(100vh - 60px)' }}>
        {/* Filtreler */}
        <div style={{ background: colors.surface, padding: '16px', borderRadius: radius.md, marginBottom: '16px', boxShadow: shadows.sm }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px' }}>
            <div>
              <label style={{ fontSize: '12px', fontWeight: '600', color: '#555', display: 'block', marginBottom: '4px' }}>Arama</label>
              <input
                type="text"
                value={filtreler.arama}
                onChange={e => handleFiltreChange('arama', e.target.value)}
                placeholder="Kullanıcı, endpoint..."
                style={{ width: '100%', padding: '8px 12px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: '14px', boxSizing: 'border-box' }}
              />
            </div>
            <div>
              <label style={{ fontSize: '12px', fontWeight: '600', color: '#555', display: 'block', marginBottom: '4px' }}>Eylem</label>
              <select
                value={filtreler.eylem}
                onChange={e => handleFiltreChange('eylem', e.target.value)}
                style={{ width: '100%', padding: '8px 12px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: '14px', boxSizing: 'border-box' }}
              >
                <option value="">Tümü</option>
                <option value="POST">POST (Oluşturma)</option>
                <option value="PUT">PUT (Güncelleme)</option>
                <option value="PATCH">PATCH (Güncelleme)</option>
                <option value="DELETE">DELETE (Silme)</option>
              </select>
            </div>
            <div>
              <label style={{ fontSize: '12px', fontWeight: '600', color: '#555', display: 'block', marginBottom: '4px' }}>Başlangıç</label>
              <input
                type="date"
                value={filtreler.baslangic_tarihi}
                onChange={e => handleFiltreChange('baslangic_tarihi', e.target.value)}
                style={{ width: '100%', padding: '8px 12px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: '14px', boxSizing: 'border-box' }}
              />
            </div>
            <div>
              <label style={{ fontSize: '12px', fontWeight: '600', color: '#555', display: 'block', marginBottom: '4px' }}>Bitiş</label>
              <input
                type="date"
                value={filtreler.bitis_tarihi}
                onChange={e => handleFiltreChange('bitis_tarihi', e.target.value)}
                style={{ width: '100%', padding: '8px 12px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: '14px', boxSizing: 'border-box' }}
              />
            </div>
          </div>
          <div style={{ marginTop: '12px', fontSize: '13px', color: colors.textSecondary }}>
            Toplam: <strong>{toplam}</strong> kayıt {loading && <span> - Yükleniyor...</span>}
          </div>
        </div>

        {/* Logs Table */}
        <div style={{ background: colors.surface, borderRadius: radius.md, overflow: 'hidden', boxShadow: shadows.sm }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#343a40', color: 'white' }}>
                  <th style={{ padding: '7px', textAlign: 'left', fontSize: '12px' }}></th>
                  <th style={{ padding: '7px', textAlign: 'left', fontSize: '12px' }}>Tarih</th>
                  <th style={{ padding: '7px', textAlign: 'left', fontSize: '12px' }}>Kullanıcı</th>
                  <th style={{ padding: '7px', textAlign: 'left', fontSize: '12px' }}>Eylem</th>
                  <th style={{ padding: '7px', textAlign: 'left', fontSize: '12px' }}>Endpoint</th>
                  <th style={{ padding: '7px', textAlign: 'left', fontSize: '12px' }}>Açıklama</th>
                  <th style={{ padding: '7px', textAlign: 'right', fontSize: '12px' }}>Status</th>
                  <th style={{ padding: '7px', textAlign: 'right', fontSize: '12px' }}>Süre</th>
                </tr>
              </thead>
              <tbody>
                {!loading && logs.length === 0 && (
                  <tr><td colSpan="8" style={{ padding: '40px', textAlign: 'center', color: colors.textTertiary }}>Kayıt bulunamadı</td></tr>
                )}
                {logs.map(log => {
                  const renk = EYLEM_RENK[log.eylem] || { bg: '#e9ecef', color: '#495057' };
                  const isExpanded = expandedRow === log.id;
                  const hasDetail = log.before_data || log.after_data || log.payload;
                  return (
                    <React.Fragment key={log.id}>
                      <tr
                        style={{ borderBottom: `1px solid ${colors.border}`, cursor: hasDetail ? 'pointer' : 'default', background: isExpanded ? '#f0f7ff' : 'white' }}
                        onClick={() => hasDetail && setExpandedRow(isExpanded ? null : log.id)}
                      >
                        <td style={{ padding: '8px 10px', fontSize: '12px', color: colors.textTertiary, width: '20px' }}>
                          {hasDetail ? (isExpanded ? '▼' : '▶') : ''}
                        </td>
                        <td style={{ padding: '8px 10px', fontSize: '12px', color: colors.textSecondary }}>{formatDateTime(log.tarih)}</td>
                        <td style={{ padding: '8px 10px', fontSize: '13px', fontWeight: '600' }}>
                          {log.kullanici_ad || log.kullanici_email || 'Bilinmiyor'}
                        </td>
                        <td style={{ padding: '8px 10px' }}>
                          <span style={{ padding: '3px 8px', borderRadius: radius.sm, fontSize: '11px', fontWeight: 'bold', background: renk.bg, color: renk.color }}>
                            {log.eylem}
                          </span>
                        </td>
                        <td style={{ padding: '8px 10px', fontSize: '12px', fontFamily: 'monospace', color: '#444', maxWidth: '350px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {log.endpoint}
                        </td>
                        <td style={{ padding: '8px 10px', fontSize: '12px', color: colors.textSecondary }}>
                          {log.aciklama || '-'}
                        </td>
                        <td style={{ padding: '8px 10px', textAlign: 'right', fontSize: '12px' }}>
                          <span style={{ color: log.status_code >= 400 ? '#dc3545' : '#28a745', fontWeight: '600' }}>
                            {log.status_code || '-'}
                          </span>
                        </td>
                        <td style={{ padding: '8px 10px', textAlign: 'right', fontSize: '12px', color: colors.textSecondary }}>
                          {log.sure_ms != null ? `${log.sure_ms}ms` : '-'}
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr style={{ background: '#fafbfc' }}>
                          <td colSpan="8" style={{ padding: '12px 20px' }}>
                            <div style={{ marginBottom: '8px', fontSize: '12px', color: colors.textSecondary }}>
                              <strong>Entity:</strong> {log.entity_type || '-'} #{log.entity_id || '-'} &nbsp;|&nbsp;
                              <strong>IP:</strong> {log.ip || '-'} &nbsp;|&nbsp;
                              <strong>User Agent:</strong> {log.user_agent ? log.user_agent.slice(0, 80) : '-'}
                            </div>
                            {renderDiff(log)}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Sayfalama */}
          <div style={{ padding: '12px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: `1px solid ${colors.border}` }}>
            <button
              onClick={() => setFiltreler(p => ({ ...p, skip: Math.max(0, p.skip - p.limit) }))}
              disabled={filtreler.skip === 0}
              style={{ padding: '6px 14px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, background: filtreler.skip === 0 ? '#f8f9fa' : 'white', cursor: filtreler.skip === 0 ? 'default' : 'pointer', fontSize: '13px' }}
            >Önceki</button>
            <span style={{ fontSize: '13px', color: colors.textSecondary }}>
              {filtreler.skip + 1} - {Math.min(filtreler.skip + filtreler.limit, toplam)} / {toplam}
            </span>
            <button
              onClick={() => setFiltreler(p => ({ ...p, skip: p.skip + p.limit }))}
              disabled={filtreler.skip + filtreler.limit >= toplam}
              style={{ padding: '6px 14px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, background: (filtreler.skip + filtreler.limit >= toplam) ? '#f8f9fa' : 'white', cursor: (filtreler.skip + filtreler.limit >= toplam) ? 'default' : 'pointer', fontSize: '13px' }}
            >Sonraki</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LogKayitlariPage;
